import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { t } from "@/lib/localization";

type RefreshedAvatar = { id: string; status: string };

export const useStartAvatarConsent = () => {
    const qc = useQueryClient();
    return useMutation<
        { consentUrl: string },
        Error,
        { id: string; rerouteUrl?: string }
    >({
        mutationKey: ["startAvatarConsent"],
        mutationFn: async ({ id, rerouteUrl }) => {
            const response = await client.api.avatars.heygen.avatars[":id"]["consent"].$post({
                param: { id },
                json: { rerouteUrl },
            });
            if (!response.ok) throw new Error("Failed to start consent");
            const json = await response.json();
            return { consentUrl: json.data.consentUrl };
        },
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: ["avatar", id] });
        },
        onError: () => {
            toast.error(t("avatars.toast.consent.error"), {
                keyName: "avatars.toast.consent.error",
            });
        },
    });
};

export const useRefreshAvatar = () => {
    const qc = useQueryClient();
    return useMutation<RefreshedAvatar, Error, { id: string }>({
        mutationKey: ["refreshAvatar"],
        mutationFn: async ({ id }) => {
            const response =
                await client.api.avatars.heygen.avatars[":id"]["refresh"].$get(
                    { param: { id } }
                );
            if (!response.ok) throw new Error("Failed to refresh avatar");
            const json = await response.json();
            return json.data.avatar;
        },
        onSuccess: (avatar) => {
            qc.invalidateQueries({ queryKey: ["avatars"] });
            qc.invalidateQueries({ queryKey: ["avatar", avatar.id] });
        },
    });
};

export type { RefreshedAvatar };
